/**
 * The exclusion ledger, rendered.
 *
 * Every Easy button owes its caller this (invariant I3 in
 * memory-bank/plans/easy-buttons.md): what was considered, what was dropped
 * and why, what is actually going to happen. One component so the discipline
 * is structurally cheap to keep and expensive to skip — a new button that
 * renders only its success count has to go out of its way to do so.
 *
 * The rule this encodes: a small result is nearly always the data talking, not
 * the tool. Three routes instead of thirty means "your roll is not geocoded"
 * or "you already planned these doors". A UI that shows only "3" leaves the
 * coordinator to guess, which is the lasso footer's "0 missing coordinates"
 * failure this repo keeps citing.
 */
import React from 'react';
import { AlertTriangle } from 'lucide-react';

import type { ProposalEnvelope } from '@/api/easy';

const n = (v: number) => v.toLocaleString();

type Props = {
  proposal: ProposalEnvelope<any>;
  /** What the denominator counts, plural. e.g. "routes", "open shifts". */
  noun: string;
  /** What "planned" means for this button. e.g. "will be assigned". */
  plannedVerb: string;
  /** Shown when the cap truncated the batch. */
  cappedNote?: string;
};

export default function ProposalLedger({
  proposal,
  noun,
  plannedVerb,
  cappedNote,
}: Props) {
  const { considered, excluded, eligible, planned, not_planned, capped } = proposal;

  return (
    <div className="rounded-md border border-border-subtle bg-bg-base p-3 text-sm">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-fg-secondary">
          {n(considered)} {noun} in reach
        </span>
        <span className="font-semibold text-fg-primary">
          {n(planned)} {plannedVerb}
        </span>
      </div>

      {excluded.length > 0 && (
        <ul className="mt-2 space-y-1 border-t border-border-subtle pt-2">
          {excluded.map((e) => (
            <li
              key={e.code}
              className="flex items-baseline justify-between gap-3 text-xs text-fg-muted"
            >
              <span>{e.label}</span>
              <span className="shrink-0 tabular-nums">−{n(e.count)}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-2 flex items-baseline justify-between gap-3 border-t border-border-subtle pt-2 text-xs text-fg-secondary">
        <span>Eligible</span>
        <span className="tabular-nums">{n(eligible)}</span>
      </div>

      {not_planned > 0 && (
        <div className="mt-1 flex items-baseline justify-between gap-3 text-xs text-fg-muted">
          <span>Left for next time</span>
          <span className="tabular-nums">{n(not_planned)}</span>
        </div>
      )}

      {capped && (
        <div className="mt-2 flex items-start gap-2 rounded border border-status-warning/40 bg-status-warning/10 p-2 text-xs text-fg-secondary">
          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-status-warning" />
          <span>
            {cappedNote ??
              `This batch was capped. ${n(not_planned)} ${noun} were left — press again for the next batch.`}
          </span>
        </div>
      )}
    </div>
  );
}
